/**
 * useCapacitorLifecycle Hook
 * 
 * React hook for subscribing to Capacitor app lifecycle events.
 * This is the recommended way for components to react to app state changes.
 * 
 * USAGE:
 * ```tsx
 * function MyComponent() {
 *   const { appState, networkState, isActive, isOnline } = useCapacitorLifecycle();
 *   
 *   // Or with callbacks for specific events
 *   useCapacitorLifecycle({
 *     onResume: () => refreshData(),
 *     onNetworkChange: (isOnline) => handleConnectivity(isOnline)
 *   });
 * }
 * ```
 * 
 * @author TotalAccess Development Team
 * @date February 7, 2026
 */

import { useEffect, useState, useRef } from 'react';
import { 
  lifecycleManager, 
  type AppLifecycleState, 
  type NetworkState, 
  type LifecycleEvent
} from '@/lib/capacitorLifecycleManager';
import { isCapacitor } from '@/lib/platformUtils';

export interface CapacitorLifecycleCallbacks {
  /** Called when app comes to foreground */
  onResume?: () => void;
  /** Called when app goes to background */
  onPause?: () => void;
  /** Called when app state changes (active/background) */
  onStateChange?: (state: AppLifecycleState, previousState: AppLifecycleState) => void;
  /** Called when network connectivity changes */
  onNetworkChange?: (isOnline: boolean, networkState: NetworkState) => void;
  /** Called on Android back button press */
  onBackButton?: () => void;
}

export interface CapacitorLifecycleState {
  /** Current app state: 'active' | 'inactive' | 'background' */
  appState: AppLifecycleState;
  /** Previous app state */
  previousAppState: AppLifecycleState;
  /** Current network state: 'online' | 'offline' | 'wifi' | 'cellular' | 'unknown' */
  networkState: NetworkState;
  /** Connection type string */
  connectionType: string;
  /** Whether app is in foreground */
  isActive: boolean;
  /** Whether app is in background */
  isBackground: boolean;
  /** Whether device has network connectivity */
  isOnline: boolean;
  /** Whether connected via WiFi */
  isWifi: boolean;
  /** Whether connected via cellular */
  isCellular: boolean;
  /** Whether running in Capacitor */
  isCapacitor: boolean;
  /** Whether lifecycle manager is initialized */
  isInitialized: boolean;
}

/**
 * Hook for subscribing to Capacitor lifecycle events
 * 
 * @param callbacks Optional callbacks for specific events
 * @returns Current lifecycle state
 */
export function useCapacitorLifecycle(callbacks?: CapacitorLifecycleCallbacks): CapacitorLifecycleState {
  const [appState, setAppState] = useState<AppLifecycleState>(lifecycleManager.getState());
  const [previousAppState, setPreviousAppState] = useState<AppLifecycleState>(lifecycleManager.getPreviousState());
  const [networkState, setNetworkState] = useState<NetworkState>(lifecycleManager.getNetworkState());
  const [connectionType, setConnectionType] = useState<string>(lifecycleManager.getConnectionType());
  const [isInitialized, setIsInitialized] = useState(lifecycleManager.isInitialized());

  // Store callbacks in ref to avoid re-subscribing on callback changes
  const callbacksRef = useRef(callbacks);

  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  useEffect(() => {
    // Initialize lifecycle manager if in Capacitor
    if (isCapacitor()) {
      lifecycleManager.initialize().then(() => {
        setIsInitialized(true);
        // Update state after initialization
        setAppState(lifecycleManager.getState());
        setNetworkState(lifecycleManager.getNetworkState());
        setConnectionType(lifecycleManager.getConnectionType());
      });
    }

    // Subscribe to all lifecycle events
    const unsubscribe = lifecycleManager.subscribe((event: LifecycleEvent) => {
      const cbs = callbacksRef.current;

      switch (event.type) {
        case 'state_change':
          if (event.state) {
            setAppState(event.state);
            setPreviousAppState(event.previousState || 'active');
            cbs?.onStateChange?.(event.state, event.previousState || 'active');
          }
          break;

        case 'resume':
          setAppState('active');
          cbs?.onResume?.();
          break;

        case 'pause':
          setAppState('background');
          cbs?.onPause?.();
          break;

        case 'network_change':
          if (event.networkState) {
            setNetworkState(event.networkState);
            setConnectionType(event.connectionType || 'unknown');
            cbs?.onNetworkChange?.(event.networkState !== 'offline', event.networkState);
          }
          break;

        case 'back_button':
          cbs?.onBackButton?.();
          break;
      }
    });

    return unsubscribe;
  }, []);

  return {
    appState,
    previousAppState,
    networkState,
    connectionType,
    isActive: appState === 'active',
    isBackground: appState === 'background',
    isOnline: networkState !== 'offline',
    isWifi: networkState === 'wifi',
    isCellular: networkState === 'cellular',
    isCapacitor: isCapacitor(),
    isInitialized
  };
}

/**
 * Hook for running an effect when app resumes from background
 * 
 * @param callback Function to run on resume
 * @param deps Dependencies array (like useEffect)
 */
export function useOnResume(callback: () => void, _deps: React.DependencyList = []): void {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!isCapacitor()) return;

    lifecycleManager.initialize();

    const unsubscribe = lifecycleManager.subscribeToTypes(['resume'], () => {
      callbackRef.current();
    });

    return unsubscribe;
  }, []);
}

/**
 * Hook for running an effect when network connectivity changes
 * 
 * @param callback Function to run on network change
 * @param deps Dependencies array (like useEffect)
 */
export function useOnNetworkChange(
  callback: (isOnline: boolean, state: NetworkState) => void, 
  _deps: React.DependencyList = []
): void {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!isCapacitor()) return;

    lifecycleManager.initialize();

    const unsubscribe = lifecycleManager.subscribeToTypes(['network_change'], (event) => {
      if (event.networkState) {
        callbackRef.current(event.networkState !== 'offline', event.networkState);
      }
    });

    return unsubscribe;
  }, []);
}

/**
 * Hook for running an effect when app goes to background
 * Useful for saving state before app is suspended
 * 
 * @param callback Function to run on pause
 * @param deps Dependencies array
 */
export function useOnPause(callback: () => void, _deps: React.DependencyList = []): void {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!isCapacitor()) return;

    lifecycleManager.initialize();

    const unsubscribe = lifecycleManager.subscribeToTypes(['pause'], () => {
      callbackRef.current();
    });

    return unsubscribe;
  }, []);
}

export default useCapacitorLifecycle;
